import React, { FunctionComponent, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import { useAcademyMFEContextStore } from "stores/v2/academyMFEContextStore";
import shallow from "zustand/shallow";
import { getClasswizeTenantKey } from "stores/classwizeConfigStore";

interface LocationState {
	logout?: boolean;
}

export const LoginRouteWrapper: FunctionComponent = () => {
	const userSessionStore = useUserSessionStore(
		(state) => ({ isAuthenticated: state.isAuthenticated, logout: state.logout, clearSession: state.clearSession }),
		shallow
	);
	const navigate = useNavigate();
	const location = useLocation();
	// This is a boolean that indicates if the user is logging out.
	// Passed from "User Settings" component when the user clicks on "Sign out"
	const logout = (location.state as LocationState)?.logout ?? false;
	// The tenant key is used to redirect the user to the School login page.
	// This will exist only if the customer has logged in and then clicks on "Sign out" from the User Settings menu.
	const tenantKey = getClasswizeTenantKey();

	useEffect(() => {
		if (logout) {
			// Clear auth info whenever any login routes are visited
			userSessionStore.logout().catch((e) => {
				// This will only work if the user is already logged in and their token is valid.
				// But we should still call it anyway to invalidate the token on the server if possible.
				console.error("Failed to logout", e);
			});
			userSessionStore.clearSession();

			// Set Masquerade to false on login
			useUserSessionStore.getState().setMasquerade(false);

			// Redirect to tenant-specific login page if tenant key is available
			if (tenantKey) {
				navigate(`/login/${tenantKey}`, { replace: true });
			}
		}

		// Initialize training params from URL after clearing session
		useAcademyMFEContextStore.getState().parseAndSetTrainingParams(window.location.href);

		if (userSessionStore.isAuthenticated()) {
			navigate("/");
			return;
		}
	}, [userSessionStore, logout, tenantKey, navigate]);

	return <Outlet />;
};
